import { Dispatch } from '@reduxjs/toolkit';
import { addCdItem } from '../../../store/order/cdBuilder';
import { generateDefaultViewItem } from 'ui-toolkit/cdItems/data/generateDefaultViewItem';
import { eUSAState } from '../../../ui-toolkit/enums/states';
import { eTitleFees } from '../../../ui-toolkit/enums/eTitleFees';
import { eSalesTaxSubTypes } from '../../../ui-toolkit/enums/eSalesTaxSubTypes';
import { eCDV3ItemTypes } from '../../../ui-toolkit/enums/eCDV3ItemTypes';
import { notEmpty } from '../../../ui-toolkit/utilities/notEmpty';

const generateAddRowActions = (
  ownerId: string,
  stateAbbreviation: string | null,
  includeAppraisalFeeAction: boolean,
  dispatch: Dispatch<any>,
  onAdd: () => void
) => ({
  dueFromBuyer: {
    adjustments: [
      {
        handler: () => {
          dispatch(
            addCdItem(ownerId, {
              ...generateDefaultViewItem(),
              dirty: true,
              new: true,
              owner_id: ownerId,
              type: eCDV3ItemTypes.buyer_reimbursement_to_third_party,
              removable: true,
            })
          );
          onAdd();
        },
        key: 'buyer-reimbursement-to-third-party',
        text: 'Buyer Reimbursement To Third Party',
      },
    ],
    adjustmentsForSeller: [
      {
        handler: () => {
          dispatch(
            addCdItem(ownerId, {
              ...generateDefaultViewItem(),
              dirty: true,
              new: true,
              owner_id: ownerId,
              type: eCDV3ItemTypes.buyer_reimbursement_other_to_seller,
              removable: true,
            })
          );
          onAdd();
        },
        key: 'buyer-reimbursement-other-to-seller',
        text: 'Buyer Reimbursement Other To Seller',
      },
    ],
  },
  initialEscrow: [
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            netted_out: true,
            new: true,
            owner_id: ownerId,
            type: eCDV3ItemTypes.escrow_at_closing_other,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'modal',
      text: 'Modal',
    },
  ],
  notShoppedFor: [
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            shopped_for: false,
            type: eCDV3ItemTypes.endorsement,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'endorsement-not-shopped-for',
      text: 'Endorsement',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            shopped_for: false,
            type: eCDV3ItemTypes.title_fee,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'title-fee-not-shopped-for',
      text: 'Title Fee',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            shopped_for: false,
            type: eCDV3ItemTypes.other_loan_cost,
            removable: true,
          })
        );

        onAdd();
      },
      key: 'other-loan-cost-not-shopped-for',
      text: 'Other Loan Cost',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            shopped_for: false,
            type: eCDV3ItemTypes.sales_tax,
            sub_type: eSalesTaxSubTypes.title_fee_lenders,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'sales-tax-not-shopped-for',
      text: 'Sales Tax',
    },
    includeAppraisalFeeAction
      ? {
          handler: () => {
            dispatch(
              addCdItem(ownerId, {
                ...generateDefaultViewItem(),
                dirty: true,
                new: true,
                owner_id: ownerId,
                shopped_for: false,
                type: eCDV3ItemTypes.appraisal_fee,
                removable: true,
              })
            );
            onAdd();
          },
          key: 'appraisal-fee-not-shopped-for',
          text: 'Appraisal Fee',
        }
      : undefined,
  ].filter(notEmpty),
  origination: [
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            netted_out: true,
            new: true,
            owner_id: ownerId,
            type: eCDV3ItemTypes.origination_fee_other,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'origination-fee-other',
      text: 'Origination Fee',
    },
  ],
  other: [
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            shopped_for: true,
            sub_type: eTitleFees.cpl_seller,
            type: eCDV3ItemTypes.title_fee,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'title-cpl-seller',
      text: 'Title - CPL Seller',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            shopped_for: true,
            sub_type: eTitleFees.standard_owners_policy,
            type: eCDV3ItemTypes.title_fee,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'title-standard-owners-policy',
      text: 'Title - Standard Owners Policy',
    },

    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            shopped_for: true,
            sub_type: eTitleFees.tieff_owners_policy,
            type: eCDV3ItemTypes.title_fee,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'title-tieff-owners-policy',
      text: 'Title - TIEFF Owners Policy',
    },

    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            shopped_for: true,
            sub_type: eTitleFees.homeowners_policy,
            type: eCDV3ItemTypes.title_fee,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'homeowners-policy',
      text: 'Title - Homeowners Policy',
    },

    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            shopped_for: true,
            sub_type: eTitleFees.owners_policy_guaranty,
            type: eCDV3ItemTypes.title_fee,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'owners-policy-guaranty',
      text: 'Title - Owners Policy Guaranty Fee',
    },
    stateAbbreviation === eUSAState.IA
      ? {
          handler: () => {
            dispatch(
              addCdItem(ownerId, {
                ...generateDefaultViewItem(),
                dirty: true,
                new: true,
                overridden: false,
                owner_id: ownerId,
                shopped_for: true,
                sub_type: eTitleFees.owners_title_guaranty,
                type: eCDV3ItemTypes.title_fee,
                removable: true,
              })
            );
            onAdd();
          },
          key: 'owners-title-guaranty',
          text: 'Title - Owners Title Guaranty',
        }
      : null,
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            type: eCDV3ItemTypes.hoa_fee,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'hoa_fee',
      text: 'HOA Fee',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            type: eCDV3ItemTypes.property_taxes,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'property_taxes',
      text: 'Property Taxes',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            shopped_for: false,
            dirty: true,
            new: true,
            owner_id: ownerId,
            type: eCDV3ItemTypes.sales_tax,
            sub_type: eSalesTaxSubTypes.title_fee_owners,
            removable: true,
          })
        );

        onAdd();
      },
      key: 'sales-tax-other',
      text: 'Sales Tax',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            type: eCDV3ItemTypes.other_fee,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'other',
      text: 'Other',
    },
  ].filter(notEmpty),
  paidAlreadyByBuyer: {
    adjustmentsForSeller: [
      {
        handler: () => {
          dispatch(
            addCdItem(ownerId, {
              ...generateDefaultViewItem(),
              dirty: true,
              new: true,
              owner_id: ownerId,
              type: eCDV3ItemTypes.buyer_reimbursement_other_from_seller,
              removable: true,
            })
          );
          onAdd();
        },
        key: 'buyer-reimbursement-other-from-seller',
        text: 'Buyer Reimbursement Other From Seller',
      },
    ],
    otherCredits: [
      {
        handler: () => {
          dispatch(
            addCdItem(ownerId, {
              ...generateDefaultViewItem(),
              dirty: true,
              new: true,
              owner_id: ownerId,
              type: eCDV3ItemTypes.buyer_credit_from_seller,
              removable: true,
            })
          );
          onAdd();
        },
        key: 'credit-from-seller',
        text: 'Credit From Seller',
      },
      {
        handler: () => {
          dispatch(
            addCdItem(ownerId, {
              ...generateDefaultViewItem(),
              dirty: true,
              new: true,
              owner_id: ownerId,
              type: eCDV3ItemTypes.buyer_credit_from_third_party,
              removable: true,
            })
          );
          onAdd();
        },
        key: 'credit-from-third-party',
        text: 'Credit From Third Party',
      },
    ],
  },
  payoffs: [
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            type: eCDV3ItemTypes.payoff,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'payoff',
      text: 'Payoff',
    },
  ],
  prepaids: [
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            type: eCDV3ItemTypes.prepayment_other,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'modal',
      text: 'Modal',
    },
  ],
  shoppedFor: [
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            shopped_for: true,
            type: eCDV3ItemTypes.endorsement,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'endorsement-shopped-for',
      text: 'Endorsement',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            shopped_for: true,
            type: eCDV3ItemTypes.title_fee_other,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'title-fee-shopped-for',
      text: 'Title Fee',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            shopped_for: true,
            type: eCDV3ItemTypes.other_loan_cost,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'other-loan-cost-shopped-for',
      text: 'Other Loan Cost',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            owner_id: ownerId,
            shopped_for: true,
            type: eCDV3ItemTypes.sales_tax,
            sub_type: eSalesTaxSubTypes.title_fee_lenders,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'sales-tax-shopped-for',
      text: 'Sales Tax',
    },
    includeAppraisalFeeAction
      ? {
          handler: () => {
            dispatch(
              addCdItem(ownerId, {
                ...generateDefaultViewItem(),
                dirty: true,
                new: true,
                owner_id: ownerId,
                shopped_for: true,
                type: eCDV3ItemTypes.appraisal_fee,
                removable: true,
              })
            );
            onAdd();
          },
          key: 'appraisal-fee-shopped-for',
          text: 'Appraisal Fee',
        }
      : undefined,
  ].filter(notEmpty),
  taxes: [
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            type: eCDV3ItemTypes.recording_fee,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'recording-fee',
      text: 'Recording Fee',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            dirty: true,
            new: true,
            overridden: false,
            owner_id: ownerId,
            type: eCDV3ItemTypes.tax,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'tax',
      text: 'Tax',
    },
    {
      handler: () => {
        dispatch(
          addCdItem(ownerId, {
            ...generateDefaultViewItem(),
            new: true,
            dirty: true,
            owner_id: ownerId,
            type: eCDV3ItemTypes.tax_other,
            removable: true,
          })
        );
        onAdd();
      },
      key: 'tax-other',
      text: 'Tax Other',
    },
  ],
});

export default generateAddRowActions;
